/**
 * The qualitativeQuestionAnswer endpoint returns `answers` either as an
 * array of answer rows or as a single answer object. Normalize to an array.
 */
export const normalizeAnswers = (answers) => {
    if (Array.isArray(answers)) return answers;
    return answers && typeof answers === "object" ? [answers] : [];
};

/**
 * Classifies a tabular question's `details` into its row/column shape — the
 * single source of truth for how the qualitative feature interprets a
 * question definition. `details` arrives in descending `detail_id` order, so
 * it is reversed once here; `rowOptions`/`colOptions` are the ascending-order
 * "row"/"column" details. The four booleans are mutually exclusive shape
 * classes: fully-dynamic (one "1" row + one "1" column), dynamic-rows /
 * fixed-columns, fixed-rows / per-row-columns (`column1`, `column2`, …), and
 * fixed-rows / fixed-columns.
 * @param {Array} details Raw question.details from the API.
 * @returns {{detailsReversed: Array, rowOptions: Array, colOptions: Array,
 *   isDynamicRows: boolean, dynamicRowsDynamicColumns: boolean,
 *   dynamicRowsFixedColumns: boolean, fixedRowsDynamicColumns: boolean,
 *   fixedRowsFixedColumns: boolean}} The reversed details and shape flags.
 */
export const classifyQuestionShape = (details) => {
    const detailsReversed = [...(details || [])].reverse();
    const rowOptions = detailsReversed.filter((d) => d.option_type === "row");
    const colOptions = detailsReversed.filter((d) => d.option_type === "column");
    const isDynamicRows = rowOptions.length === 1 && rowOptions[0].option === "1";
    const dynamicRowsDynamicColumns =
        isDynamicRows && colOptions.length === 1 && colOptions[0].option === "1";
    const dynamicRowsFixedColumns = isDynamicRows && !dynamicRowsDynamicColumns;
    const fixedRowsDynamicColumns = detailsReversed.some((d) => /^column\d+$/.test(d.option_type));
    const fixedRowsFixedColumns =
        !dynamicRowsDynamicColumns && !dynamicRowsFixedColumns && !fixedRowsDynamicColumns;
    return {
        detailsReversed,
        rowOptions,
        colOptions,
        isDynamicRows,
        dynamicRowsDynamicColumns,
        dynamicRowsFixedColumns,
        fixedRowsDynamicColumns,
        fixedRowsFixedColumns,
    };
};

/**
 * Column details for a fixed row at `rIndex`, from a `classifyQuestionShape`
 * result. Per-row-column questions read `column{rIndex+1}`; all other fixed
 * questions share the single `column` set.
 * @param {object} shape Result of `classifyQuestionShape`.
 * @param {number} rIndex Zero-based row index.
 * @returns {Array} The column details for that row.
 */
export const columnsForRow = (shape, rIndex) =>
    shape.detailsReversed.filter(
        (d) => d.option_type === (shape.fixedRowsDynamicColumns ? `column${rIndex + 1}` : "column")
    );

/**
 * Conforms a stored answer grid to the question's current row/column
 * definition. Fixed-row questions render one grid row per "row" detail, so an
 * answer saved under an older question shape (different row/column counts, or
 * the fully-dynamic format with a leading string-header row) must be
 * padded/truncated before rendering — otherwise row labels misalign against
 * `rowOptions` and stray rows render unlabeled. Dynamic-row questions own
 * their stored shape and pass through unchanged. Grouped (3-D) answers are
 * conformed per column group; primitive cells from legacy shapes are wrapped
 * into `{ value, unit_id }` objects so their values are preserved.
 */
export const conformGridToQuestion = (grid, details, columnGroups = null) => {
    const shape = classifyQuestionShape(details);
    const { rowOptions, isDynamicRows } = shape;
    if (rowOptions.length === 0 || isDynamicRows) return grid;

    const emptyCell = () => ({ value: "", unit_id: null });
    const toCell = (cell) => {
        if (cell && typeof cell === "object" && !Array.isArray(cell)) return cell;
        if (typeof cell === "string" || typeof cell === "number") {
            return { value: String(cell), unit_id: null };
        }
        return emptyCell();
    };
    const conformCells = (cells, count) =>
        Array.from({ length: count }, (_, i) => toCell((cells || [])[i]));

    const grouped =
        Array.isArray(columnGroups) && columnGroups.length > 0 && Array.isArray(grid?.[0]?.[0]);

    const isHeaderRow = (row) =>
        Array.isArray(row) &&
        row.length > 0 &&
        (grouped
            ? row.every((g) => Array.isArray(g) && g.every((c) => typeof c === "string"))
            : row.every((c) => typeof c === "string"));

    const dataRows = Array.isArray(grid) ? [...grid] : [];
    if (isHeaderRow(dataRows[0])) dataRows.shift();

    return rowOptions.map((_, rIndex) => {
        const colCount = Math.max(columnsForRow(shape, rIndex).length, 1);
        const rowVals = dataRows[rIndex];
        if (grouped) {
            return columnGroups.map((_g, gIdx) => conformCells((rowVals || [])[gIdx], colCount));
        }
        return conformCells(rowVals, colCount);
    });
};

/**
 * Counts non-empty `{ value }` cells anywhere in a stored answer grid (2-D or
 * 3-D). Header strings and blank cells are ignored — whitespace-only values
 * count as blank, matching the backend `countQualitativeValues` check so the
 * save guard and the "data will be removed" warning agree across the stack.
 * Comparing the count before and after `conformGridToQuestion` reveals whether
 * conforming dropped real data (rows/cells outside the question's current shape).
 */
export const countGridValues = (grid) => {
    if (!Array.isArray(grid)) return 0;
    return grid.reduce((sum, item) => {
        if (Array.isArray(item)) return sum + countGridValues(item);
        const filled =
            item &&
            typeof item === "object" &&
            item.value !== null &&
            item.value !== undefined &&
            String(item.value).trim() !== "";
        return sum + (filled ? 1 : 0);
    }, 0);
};
